import Head from 'next/head';
import { useRouter } from 'next/router';
import { ReactNode } from 'react';
import { Merge } from '../src/utils/types';
import { localizePath } from '../src/utils/localizedRoutes';
import { twMerge } from 'tailwind-merge';
import Footer from './layouts/Footer';

const DOMAIN = process.env.NEXT_PUBLIC_URL
export interface CorePageProps {
  title: string
  description: string
  className?: string
  footer?: boolean
  structuredData?: Record<string, unknown>
  noIndex?: boolean
  children: ReactNode
}

type PageProps = Merge<React.ComponentProps<'div'>, CorePageProps>

export default function Page({
  title,
  description,
  className,
  footer = false,
  structuredData,
  noIndex = false,
  children,
  ...passthroughProps
}: PageProps) {

  const router = useRouter();

  const pageTitle = title.concat(" | VisualOrc")
  // router.pathname is always the pt-worded file route (e.g.
  // /software/planeamento/criar-o-plano-de-compras), shared across every
  // locale by the Pages Router. localizePath() swaps it for that locale's
  // translated, pretty URL (see src/utils/localizedRoutes.ts) so canonical
  // and hreflang tags never point at a pt-worded URL for a translated page.
  // router.pathname is "/" for the homepage; appending it after a locale prefix
  // would produce "/en/" which trailingSlash:false then 308-redirects to "/en",
  // so it must be dropped there to avoid canonical/hreflang pointing at a redirect.
  const localizedPathFor = (l: string) =>
    router.pathname === "/" ? "" : localizePath(router.pathname, l)
  let canonical = router.locale && router.locale !== router.defaultLocale
    ? DOMAIN.concat("/", router.locale, localizedPathFor(router.locale))
    : DOMAIN.concat(router.pathname)
  let alternateDefault = DOMAIN.concat(router.pathname)
  let alternates = router.locales.map(l => {
    return {
      locale: l,
      url: l == router.defaultLocale ? DOMAIN.concat(router.pathname) : DOMAIN.concat("/", l, localizedPathFor(l))
    }
  })

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
        <meta name="description" content={description} />
        {noIndex && <meta name="robots" content="noindex" />}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
        <meta property="og:locale" content={router.locale} />
        <link rel="canonical" href={canonical} />
        <link rel="alternate" hrefLang="x-default" href={alternateDefault} />
        {alternates.map((a, i) => <link key={i} rel="alternate" hrefLang={a.locale} href={a.url} />)}
        {structuredData && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
          />
        )}
      </Head>
      <div className='min-h-100vh flex flex-col'>
        <div
          className={twMerge(
            'w-full h-full pt-28 md:pt-36 pb-8 md:pb-16 px-4 md:px-6 flex-1',
            className
          )}
          {...passthroughProps}
        >
          {children}
        </div>
        {footer && <Footer />}
      </div>
    </>
  )
}
